import Modal from 'react-bootstrap/Modal';
import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getConfigValue } from '../util/getConfigValue';
import { AppContext } from '../util/AppContext';
import { trackDocumentRequest, trackingEventType } from '../API/documentGenerate';
import { useErrorBoundary } from 'react-error-boundary';
import PdfViewerComponent from './PdfViewerComponent';
import { FavoriteDesignation, updateHcpAppState } from '../components/FavoriteDesignation';
import { getPrescriberById } from '../API/prescriber';

PreviewModal.propTypes = {
	show: PropTypes.bool.isRequired,
	setShow: PropTypes.func.isRequired,
	onGenerateClick: PropTypes.func,
	isValid: PropTypes.bool,
	isLoading: PropTypes.bool.isRequired,
	pdfUrl: PropTypes.string,
	showGenerateBack: PropTypes.bool.isRequired,
	showPrintDownload: PropTypes.bool.isRequired,
	pdfFileName: PropTypes.string.isRequired,
	supportsProfessionalPrinting: PropTypes.bool.isRequired,
	skipPreviewTrack: PropTypes.bool,
	provider: PropTypes.string,
	sellSheetData: PropTypes.object,
	territory: PropTypes.string,
	asModal: PropTypes.bool,
	showProviderTerritory: PropTypes.bool,
	isQuickSheet: PropTypes.bool,
	quickSheetId: PropTypes.string,
	returnURL: PropTypes.string,
};

PreviewModal.defaultProps = {
	showProviderTerritory: true,
};

function PreviewModal({
	show,
	setShow,
	onGenerateClick,
	isValid,
	isLoading,
	pdfUrl,
	showGenerateBack,
	showPrintDownload,
	pdfFileName,
	supportsProfessionalPrinting,
	skipPreviewTrack,
	provider,
	territory,
	sellSheetData,
	asModal = true,
	showProviderTerritory = true,
	isQuickSheet = false,
	returnURL,
	quickSheetId
}) {
	const [appState, setAppState] = useContext(AppContext);
	const [hcp, setHcp] = useState(appState?.prescriber || null);
	const { showBoundary } = useErrorBoundary();
	const canDownload = getConfigValue(appState.config?.Generate?.Document?.Download?.Enabled, appState)?.Value === 'true' || false;
	const canPrint = getConfigValue(appState.config?.Generate?.Document?.Print?.Enabled, appState)?.Value === 'true' || false;
	const canSendToVendor = supportsProfessionalPrinting;

	const navigate = useNavigate();
	
	const isDocumentReady = !isLoading && pdfUrl && pdfFileName && ((!isQuickSheet && appState.sellSheetId) || (isQuickSheet && quickSheetId));
	useEffect(() => {
		if (appState?.prescriber?.id) {
			setHcp(appState?.prescriber);
			return;
		}
		const { prescriberId, marketId, territoryId } = sellSheetData?.documentGenerationRequest || {};
		if (!prescriberId || !marketId || !territoryId) {
			return;
		}

		getPrescriberById(prescriberId, marketId, territoryId)
			.then((data) => {
				if (data) {
					setHcp(data);
				}
			})
			.catch((error) => {
				console.error('Error fetching prescriber data:', error);
			});
		
	}, [sellSheetData?.documentGenerationRequest?.prescriberId, appState?.prescriber]);

	useEffect(() => {
		const previewFile = async (url) => {
			if (skipPreviewTrack || (!appState.lastGeneratedDocument && !isQuickSheet) || (isQuickSheet && !quickSheetId)) {
				return;
			}
			// track preview request
			if(isQuickSheet){
				trackDocumentRequest(trackingEventType.QuickSheetPreview, null, null, quickSheetId);	
			}else{
				trackDocumentRequest(trackingEventType.Preview, appState.lastGeneratedDocument, appState.sellSheetId, null);
			}
			
		};

		if (pdfUrl) {
			try {
				previewFile(pdfUrl);
			} catch (e) {
				showBoundary(e);
			}
		}
	}, [pdfUrl, isQuickSheet, quickSheetId]);

	function updateFavorite(data) {
		//if it came from appState, update there
		if (appState?.prescriber?.id) {
			updateHcpAppState(data, setAppState);
			return;
		}
		//if it came from local state, update there
		setHcp((prevHcp) => ({ ...prevHcp, favorite: !data.favorite }));
	}
	function onPrintClick(event) {
		event.preventDefault();
		if (pdfUrl) {
			try {
				// track print request
				if (!isQuickSheet && appState.lastGeneratedDocument) {
					trackDocumentRequest(trackingEventType.Print, appState.lastGeneratedDocument, appState.sellSheetId, null);
				}
				if (isQuickSheet && quickSheetId) {
					trackDocumentRequest(trackingEventType.QuickSheetPrint, null, null, quickSheetId);
				}
				window.open(pdfUrl, 'PRINT');
			} catch (e) {
				showBoundary(e);
			}
		}
	}

	function onDownloadClick() {
		if (pdfUrl) {
			try {
				// track download request
				if (!isQuickSheet && appState.lastGeneratedDocument) {
					trackDocumentRequest(trackingEventType.DownLoad, appState.lastGeneratedDocument, appState.sellSheetId, null);
				}
				if (isQuickSheet && quickSheetId) {
					trackDocumentRequest(trackingEventType.QuickSheetDownload, null, null, quickSheetId);
				}

				const link = document.createElement('a');
				link.href = pdfUrl;
				link.download = pdfFileName;
				link.click();
			} catch (e) {
				showBoundary(e);
			}
		}
	}

	function onCreateProfessionalPrintingDocument() {
		if (!isDocumentReady) {
			return;
		}
		navigate('/createProfessionalPrintingDocument', { state: { pdfURL: pdfUrl, fileName: pdfFileName, isQuickSheet: isQuickSheet, returnURL: returnURL, quickSheetId: quickSheetId } });
	}

	const renderComponent = () => {
		return (
			<>
				{asModal ? (
					<Modal show={show} onHide={() => setShow(false)} size='xl' className='modal-preview'>
						<Modal.Header className='primary' closeButton>
							<Modal.Title className='type-18'>{getConfigValue(appState.config.Preview?.Header, appState)?.Value}</Modal.Title>
						</Modal.Header>
						<Modal.Body>{renderBody()}</Modal.Body>
					</Modal>
				) : (
					<div className={'bg-color-precision-white p-3'}>{renderBody()}</div>
				)}
			</>
		);
	};

	const renderBody = () => {
		return (
			<>
				{showGenerateBack && (
					<div className='row gx-3 my-2 mb-3 justify-content-end'>
						<div className='col-6 col-lg-4'>
							<button onClick={() => setShow(false)} className='btn btn-secondary btn-icon mt-2 w-100'>
								{getConfigValue(appState.config.Preview?.Back, appState)?.Value}
							</button>
						</div>
						<div className='col-6 col-lg-4'>
							<button
								disabled={!isValid || isLoading}
								onClick={onGenerateClick}
								className={`btn btn-success mt-2 w-100 ${isValid || isLoading ? '' : 'disabled'} ${isLoading ? 'btn-working' : ''}`}
							>
								{' ' + getConfigValue(appState.config.Preview?.Generate, appState)?.Value}
							</button>
						</div>
					</div>
				)}
				{showPrintDownload && (
					<div className='row gx-3 my-2 mb-3 align-items-start'>
						<div className='col-12 col-lg-5 mb-3 mb-lg-0'>
							{showProviderTerritory && (
								<div className='d-flex flex-wrap gap-5'>
									<div>
										<div className='color-grey-d1 small'>
											{getConfigValue(appState.config?.GenerateDocumentPage?.SelectedProvider?.Label, appState)?.Value || 'Prescriber'}
										</div>
										<div className='favorite-hcp'>
											{hcp?.id && <FavoriteDesignation hcp={hcp} updateCallback={() => updateFavorite(hcp)}></FavoriteDesignation>}
											{provider}
										</div>
									</div>
									<div>
										<div className='color-grey-d1 small'>
											{getConfigValue(appState.config?.GenerateDocumentPage?.SelectedTerritory?.Label, appState)?.Value || 'Territory'}
										</div>
										<div>{territory}</div>
									</div>
								</div>
							)}
						</div>
						<div className='col-12 col-lg-7'>
							<div className='d-flex gap-3 flex-column flex-lg-row justify-content-end'>
								{canSendToVendor && (
									<button
										disabled={!isDocumentReady}
										onClick={onCreateProfessionalPrintingDocument}
										className={`btn btn-outline-primary btn-icon w-100 ${isLoading ? 'btn-working' : ''}`}
									>
										{getConfigValue(appState.config?.ProfessionalPrinting?.SendToPrint, appState)?.Value}
										<span className='fa-regular fa-paper-plane btn-icon'></span>
									</button>
								)}
								{canPrint && (
									<button
										disabled={!isDocumentReady}
										onClick={onPrintClick}
										className={`btn btn-outline-primary btn-icon w-100 ${isLoading ? 'btn-working' : ''}`}
									>
										{getConfigValue(appState.config.Preview?.Print, appState)?.Value}
										<span className='fa-regular fa-print btn-icon'></span>
									</button>
								)}
								{canDownload && (
									<button
										disabled={!isDocumentReady}
										onClick={onDownloadClick}
										className={`btn btn-outline-primary btn-icon w-100 mt-3 mt-lg-0 ${isLoading ? 'btn-working' : ''}`}
									>
										{getConfigValue(appState.config.Preview?.Download, appState)?.Value}
										<span className='fa-regular fa-download btn-icon'></span>
									</button>
								)}
							</div>
						</div>
					</div>
				)}

				<hr className='my-4' />
				<PdfViewerComponent document={pdfUrl} />
			</>
		);
	};

	return <>{renderComponent()}</>;
}

export default PreviewModal;
